import { useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, Tooltip, Filler,
} from "chart.js";
import {Container} from '../CalculadoraJurosEquivalente/styles';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Filler);

/* ----------------------------- helpers ----------------------------- */
const fmt = (n) => {
  if (Math.abs(n) >= 1e6) return "R$ " + (n / 1e6).toFixed(2).replace(".", ",") + "M";
  if (Math.abs(n) >= 1e3) return "R$ " + (n / 1e3).toFixed(0) + "k";
  return "R$ " + Math.round(n).toLocaleString("pt-BR");
};
const fmtFull = (n) => "R$ " + Math.round(n || 0).toLocaleString("pt-BR");
const roundStep = (n, step) => Math.round(n / step) * step;
const num = (v, fallback) => { const n = parseFloat(v); return Number.isFinite(n) ? n : fallback; };

/* --------- núcleo de cálculo: só depende dos 6 inputs --------- */
function buildBase(inputs) {
  const renda = num(inputs.renda, 15000);
  const patInicial = num(inputs.patInicial, 0);
  const aporte = num(inputs.aporte, 5000);
  const taxaAcumAnual = num(inputs.taxaAcum, 6) / 100;
  const taxaRetiraAnual = num(inputs.taxaRetira, 5) / 100;
  const anos = Math.max(1, Math.round(num(inputs.anos, 25)));

  const taxaSeguraReferencia = Math.min(0.06, taxaRetiraAnual);
  const taxaMensal = Math.pow(1 + taxaAcumAnual, 1 / 12) - 1;
  const meses = anos * 12;

  let patrimonio = patInicial;
  let patInicialCorrigido = patInicial;
  let totalAportado = 0;
  const acumLabels = [], acumAportesData = [], acumRendimentoData = [], acumPatInicialData = [];

  for (let m = 0; m <= meses; m++) {
    if (m % 12 === 0) {
      acumLabels.push("Ano " + Math.round(m / 12));
      acumAportesData.push(Math.round(totalAportado));
      acumPatInicialData.push(Math.round(patInicialCorrigido));
      acumRendimentoData.push(Math.round(Math.max(0, patrimonio - totalAportado - patInicialCorrigido)));
    }
    patInicialCorrigido = patInicialCorrigido * (1 + taxaMensal);
    patrimonio = patrimonio * (1 + taxaMensal) + aporte;
    totalAportado += aporte;
  }

  const last = acumAportesData.length - 1;
  const patrimonioFinal = acumPatInicialData[last] + acumAportesData[last] + acumRendimentoData[last];
  const rendaMensalReferencia = (patrimonioFinal * taxaSeguraReferencia) / 12;
  const patrimonioNecessario = (renda * 12) / taxaSeguraReferencia;
  const sugestaoMax = Math.max(roundStep(rendaMensalReferencia * 2.5, 500), 5000);

  return { renda, patInicial, aporte, anos, taxaAcumAnual, taxaRetiraAnual, taxaSeguraReferencia,
    acumLabels, acumAportesData, acumRendimentoData, acumPatInicialData,
    totalAportado: Math.round(totalAportado), patrimonioFinal, rendaMensalReferencia,
    patrimonioNecessario, sugestaoMax };
}

/* --------- fase de retirada: base + retirada escolhida --------- */
function buildRetira(base, retiradaMensalEscolhida) {
  const { patrimonioFinal, taxaSeguraReferencia, taxaRetiraAnual } = base;
  const retiraLabels = ["Aposentadoria"];
  const retiraSeguraData = [patrimonioFinal], retiraCustomData = [patrimonioFinal];
  let patSegura = patrimonioFinal, patCustom = patrimonioFinal, anosAcabouCustom = null;
  const retiradaSeguraAnual = patrimonioFinal * taxaSeguraReferencia;
  const retiradaCustomAnual = retiradaMensalEscolhida * 12;

  for (let a = 1; a <= 60; a++) {
    if (patSegura > 0) patSegura = Math.max(0, patSegura * (1 + taxaRetiraAnual) - retiradaSeguraAnual);
    if (patCustom > 0) {
      patCustom = Math.max(0, patCustom * (1 + taxaRetiraAnual) - retiradaCustomAnual);
      if (patCustom === 0 && anosAcabouCustom === null) anosAcabouCustom = a;
    }
    retiraLabels.push("Ano " + a);
    retiraSeguraData.push(Math.round(patSegura));
    retiraCustomData.push(Math.round(patCustom));
  }
  return { retiraLabels, retiraSeguraData, retiraCustomData, retiradaSeguraAnual, retiradaCustomAnual, anosAcabouCustom };
}

/* ------------------------------ componente ------------------------------ */
export default function SimuladorAposentadoria() {
  const [inputs, setInputs] = useState({
    renda: "15000", patInicial: "0", aporte: "5000", anos: "25", taxaAcum: "6", taxaRetira: "5",
  });
  const [sliderValue, setSliderValue] = useState(null); // null = não tocado
  const setInput = (key) => (e) => setInputs((s) => ({ ...s, [key]: e.target.value }));

  const base = useMemo(() => buildBase(inputs), [inputs]);

  const retiradaEscolhida = sliderValue === null
    ? roundStep(base.rendaMensalReferencia, 500)
    : Math.min(sliderValue, base.sugestaoMax);

  const retira = useMemo(() => buildRetira(base, retiradaEscolhida), [base, retiradaEscolhida]);

  const pct = (base.taxaSeguraReferencia * 100).toFixed(1);
  const metaAtingida = base.patrimonioFinal >= base.patrimonioNecessario;

  const acumDatasets = [];
  if (base.patInicial > 0) acumDatasets.push({ label: "Patrimônio inicial corrigido", data: base.acumPatInicialData, backgroundColor: "#1baf7a", borderRadius: 3, stack: "stack" });
  acumDatasets.push(
    { label: "Total aportado", data: base.acumAportesData, backgroundColor: "#b5d4f4", borderRadius: 3, stack: "stack" },
    { label: "Rendimento acumulado", data: base.acumRendimentoData, backgroundColor: "#2a78d6", borderRadius: 3, stack: "stack" }
  );
  const acumData = { labels: base.acumLabels, datasets: acumDatasets };
  const acumOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => " " + ctx.dataset.label + ": " + fmtFull(ctx.raw) } } },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: "#888781", font: { size: 11 }, maxRotation: 0 } },
      y: { stacked: true, grid: { color: "#e1e0d9" }, ticks: { color: "#888781", font: { size: 11 }, callback: (v) => fmt(v) } },
    },
  };

  const retiraData = {
    labels: retira.retiraLabels,
    datasets: [
      { label: `Retirada segura (${pct}% a.a.)`, data: retira.retiraSeguraData, borderColor: "#2a78d6", backgroundColor: "rgba(42,120,214,0.08)", fill: true, tension: 0.3, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4 },
      { label: "Retirada escolhida", data: retira.retiraCustomData, borderColor: "#e34948", borderDash: [6, 3], backgroundColor: "transparent", fill: false, tension: 0.3, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4 },
    ],
  };
  const retiraOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => " " + ctx.dataset.label + ": " + fmtFull(ctx.raw) } } },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#888781", font: { size: 11 }, maxTicksLimit: 10, maxRotation: 0 } },
      y: { grid: { color: "#e1e0d9" }, ticks: { color: "#888781", font: { size: 11 }, callback: (v) => fmt(v) } },
    },
  };

  return (
    <Container>
        <div className="sim-root">
        <div className="sim-container">
            <h1 className="sim-title">Simulador de Aposentadoria</h1>
            <p className="sim-subtitle">Renda real, protegida da inflação — projeção de acumulação e retirada</p>

            <div className="sim-grid">
              <Field label="Renda mensal desejada (R$)" hint="Valor em reais de hoje (já deflacionado)">
                  <input type="number" min="500" step="500" value={inputs.renda} onChange={setInput("renda")} />
              </Field>
              <Field label="Patrimônio inicial (R$)" hint="Quanto você já tem investido hoje">
                  <input type="number" min="0" step="1000" value={inputs.patInicial} onChange={setInput("patInicial")} />
              </Field>
              <Field label="Aporte mensal (R$)">
                  <input type="number" min="100" step="100" value={inputs.aporte} onChange={setInput("aporte")} />
              </Field>
              <Field label="Anos até se aposentar">
                  <input type="number" min="1" max="50" step="1" value={inputs.anos} onChange={setInput("anos")} />
              </Field>
              <Field label="Taxa real na acumulação (%)" hint="Carteira mais arrojada, já sem inflação">
                  <input type="number" min="0.1" max="20" step="0.1" value={inputs.taxaAcum} onChange={setInput("taxaAcum")} />
              </Field>
              <Field label="Taxa real na aposentadoria (%)" hint="Carteira normalmente mais conservadora nessa fase">
                  <input type="number" min="0.1" max="20" step="0.1" value={inputs.taxaRetira} onChange={setInput("taxaRetira")} />
              </Field>
            </div>

            <div className="kpi-row">
            <div className={"kpi " + (metaAtingida ? "kpi--ok" : "kpi--danger")}>
                <div className="klabel">Patrimônio acumulado</div>
                <div className="kval">{fmtFull(base.patrimonioFinal)}</div>
                <div className="ksub">{metaAtingida ? "Meta atingida ✓" : "Abaixo da meta"}</div>
            </div>
            <div className="kpi">
                <div className="klabel">Renda segura ({pct}% a.a.)</div>
                <div className="kval">{fmtFull(base.rendaMensalReferencia)}</div>
                <div className="ksub">Renda real protegida da inflação</div>
            </div>
            <div className="kpi">
                <div className="klabel">Patrimônio necessário para meta</div>
                <div className="kval">{fmtFull(base.patrimonioNecessario)}</div>
                <div className="ksub">Para retirar {fmtFull(base.renda)}/mês a {pct}% a.a.</div>
            </div>
            <div className="kpi">
                <div className="klabel">Total aportado</div>
                <div className="kval">{fmtFull(base.totalAportado)}</div>
                <div className="ksub">Juros s/ aportes: {fmtFull(base.acumRendimentoData[base.acumRendimentoData.length - 1])}</div>
            </div>
            </div>

            <div className="section-title">Evolução do patrimônio — fase de acumulação</div>
            <div className="legend">
            <span><i style={{ background: "#b5d4f4" }} />Total aportado</span>
            <span><i style={{ background: "#2a78d6" }} />Rendimento acumulado</span>
            {base.patInicial > 0 && <span><i style={{ background: "#1baf7a" }} />Patrimônio inicial corrigido</span>}
            </div>
            <div className="chart-wrap"><Bar data={acumData} options={acumOptions} /></div>

            <hr className="divider" />

            <div className="section-title">Fase de retirada — quanto tempo o patrimônio dura?</div>
            <div className="withdraw-row">
            <label>Retirada mensal desejada</label>
            <input type="range" min="500" max={base.sugestaoMax} step="500" value={retiradaEscolhida}
                onChange={(e) => setSliderValue(parseFloat(e.target.value))} />
            <span className="val">{fmtFull(retiradaEscolhida)}</span>
            </div>
            <div className="withdraw-refs">Referência segura: {fmtFull(base.rendaMensalReferencia)}/mês ({pct}% a.a.)</div>

            {base.taxaRetiraAnual < 0.06 && (
            <div className="warn-box">
                A taxa real na aposentadoria ({(base.taxaRetiraAnual * 100).toFixed(1)}%) está abaixo do benchmark clássico de 6%.
                Por isso a retirada segura de referência foi ajustada para {fmtFull(base.rendaMensalReferencia)}/mês — retirar no
                ritmo de 6% nessa carteira consumiria o principal.
            </div>
            )}

            {retira.retiradaCustomAnual <= retira.retiradaSeguraAnual ? (
            <div className="safe-box">Patrimônio sustentável indefinidamente. Retirando {fmtFull(retiradaEscolhida)}/mês o principal não é consumido.</div>
            ) : (
            <div className="danger-box">
                {retira.anosAcabouCustom
                ? `Retirando ${fmtFull(retiradaEscolhida)}/mês — acima da referência segura de ${fmtFull(base.rendaMensalReferencia)}/mês — o patrimônio se esgota em aproximadamente ${retira.anosAcabouCustom} anos após a aposentadoria.`
                : `Retirando ${fmtFull(retiradaEscolhida)}/mês — acima da referência segura de ${fmtFull(base.rendaMensalReferencia)}/mês — mas o patrimônio ainda dura mais de 60 anos nesse ritmo.`}
            </div>
            )}

            <div className="legend">
            <span><i style={{ background: "#2a78d6" }} />Retirada segura de referência ({pct}% a.a.)</span>
            <span><i style={{ border: "none", borderTop: "2px dashed #e34948", width: 18, height: 0 }} />Retirada escolhida</span>
            </div>
            <div className="chart-wrap" style={{ height: 240 }}><Line data={retiraData} options={retiraOptions} /></div>
        </div>
        <style>{css}</style>
        </div>
    </Container>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="sim-field">
      <label>{label}</label>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

const css = `
    .sim-root{--surface-0:#f7f7f5;--surface-1:#f0efeb;--surface-2:#fff;--text-primary:#0b0b0b;--text-secondary:#52514e;--text-muted:#898781;--text-accent:#0c447c;--text-danger:#791f1f;--text-success:#085041;--text-warning:#633806;--bg-accent:#e6f1fb;--bg-danger:#fcebeb;--bg-success:#eaf3de;--bg-warning:#faeeda;--border:rgba(11,11,11,.1);--border-danger:#f0997b;--border-success:#97c459;--border-warning:#ef9f27;--radius:8px;--font-sans:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;background:var(--surface-0);font-family:var(--font-sans);color:var(--text-primary);padding:0;}
    .sim-root *{box-sizing:border-box;}
    .sim-container{max-width:auto;margin:0 auto;background:white;padding:12px 14px 16px;}
    .sim-title{font-size:20px;font-weight:600;margin:0 0 4px;color: #1C2D38;}
    .sim-subtitle{font-size:13px;color:var(--text-secondary);margin:0 0 1.5rem;}
    .sim-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:1.5rem;}
    .sim-field{display:flex;flex-direction:column;gap:6px;}
    .sim-field label{
        font-size:13px;
        color: #253A4A;
        font-weight:500;
    }

    .sim-field input[type=number]{
        width:100%;
        padding:8px 10px;
        border:.5px solid var(--border);
        border-radius:var(--radius);
        font-size:14px;
        background:var(--surface-2);
        color:var(--text-primary);
        transition:border-color .2s ease, box-shadow .2s ease;
    }

    .sim-field input[type=number]:hover{
        border-color:#EC6643;
    }

    .sim-field input[type=number]:focus{
        outline:none;
        border-color:#EC6643;
        box-shadow:0 0 0 3px rgba(236,102,67,.20);
    }
    .sim-field .hint{font-size:11px;color: #40444C;margin-top:2px;}
    .kpi-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px;margin-bottom:1.5rem;}
    .kpi{background:var(--surface-1);border-radius:var(--radius);padding:12px 14px;}
    .kpi--ok{background:var(--bg-success);border:.5px solid var(--border-success);}
    .kpi--ok .kval,.kpi--ok .ksub{color:var(--text-success);}
    .kpi--danger{background:var(--bg-danger);border:.5px solid var(--border-danger);}
    .kpi--danger .kval,.kpi--danger .ksub{color:var(--text-danger);}
    .kpi .klabel{font-size:12px;color:var(--text-muted);margin-bottom:4px;}
    .kpi .kval{font-size:20px;font-weight:600;color:var(--text-primary);}
    .kpi .ksub{font-size:11px;color:var(--text-secondary);margin-top:2px;}
    .section-title{font-size:13px;font-weight:600;color:var(--text-secondary);margin:1.5rem 0 .5rem;text-transform:uppercase;letter-spacing:.04em;}
    .chart-wrap{position:relative;width:100%;height:260px;margin-bottom:1.5rem;}
    .legend{display:flex;flex-wrap:wrap;gap:14px;margin-bottom:1rem;font-size:12px;color:var(--text-secondary);}
    .legend span{display:flex;align-items:center;gap:5px;}
    .legend i{width:12px;height:3px;border-radius:2px;display:inline-block;}
    .withdraw-row{display:flex;align-items:center;gap:12px;margin-bottom:.5rem;}
    .withdraw-row label{font-size:13px;color:var(--text-secondary);white-space:nowrap;font-weight:500;}
    .withdraw-row input[type=range]{flex:1;accent-color:#2a78d6;}
    .withdraw-row .val{font-size:15px;font-weight:600;min-width:100px;text-align:right;}
    .withdraw-refs{font-size:11px;color: #40444C;margin-bottom:1rem;}
    .danger-box{background:var(--bg-danger);border:.5px solid var(--border-danger);border-radius:var(--radius);padding:10px 14px;font-size:13px;color:var(--text-danger);margin-bottom:1rem;}
    .safe-box{background:var(--bg-success);border:.5px solid var(--border-success);border-radius:var(--radius);padding:10px 14px;font-size:13px;color:var(--text-success);margin-bottom:1rem;}
    .warn-box{background:var(--bg-warning);border:.5px solid var(--border-warning);border-radius:var(--radius);padding:10px 14px;font-size:13px;color:var(--text-warning);margin-bottom:1rem;}
    .divider{border:none;border-top:.5px solid var(--border);margin:1.5rem 0;}
    @media (max-width:600px){.sim-root{padding:12px;}.sim-container{padding:16px 16px 24px;}.sim-grid{grid-template-columns:1fr;}}
`;